// ================================================================
// src/pages/admin/AdminProjectReview.jsx
// ================================================================
// صفحه‌ی بررسی عمیق مدیر — قلب مدیریت محتوا
// ----------------------------------------------------------------
// دو حالت اصلی:
//   ۱. mode = 'request'  → بررسی درخواست معلمی جدید (formData + سابقه)
//   ۲. mode = 'project'  → بررسی محتوای پروژه (نسخه‌ها + diff + impact)
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. نمایش کامل محتوای نسخه (chapters/lessons/steps/videos)
//   ۲. انتخابگر نسخه (published / pending / history)
//   ۳. Diff Viewer برای مقایسه‌ی نسخه‌ها
//   ۴. Impact Analyzer برای تأثیر بر دانش‌آموزان
//   ۵. Timeline تاریخچه‌ی نسخه‌ها
//   ۶. ویرایش inline توسط مدیر (به نمایندگی معلم)
//   ۷. پیش‌نمایش به‌عنوان دانش‌آموز
//   ۸. Approve/Reject/Request-Edit/Rollback
//   ۹. Audit Trail این پروژه
// ================================================================

import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';

import {
  BooksEngineProvider,
  useBooksEngine, useEngineReady,
  useProject, useRequest, useTeacherByUserId,
  useProjectVersions, useVersion, useVersionActions,
  useAdminRequestActions, useProjectActions,
  useDiff, useImpactAnalysis, useAuditLog,
  useKeyboardShortcuts,
  REQUEST_STATUS, PROJECT_STATE, VERSION_STATUS,
  AUDIT_ACTION,
  GRADE_TITLES, SUBJECT_LABELS,
} from '../../hooks/useBooksEngine.js';

import dataEngine from '../../core/books/data.js';
import { auditLog } from '../../core/books/logic.js';

import {
  Spinner, Badge, Chip, EmptyState, SkeletonLoader, LockBanner,
  Modal, ConfirmModal,
  BlobImage, VideoPreview, DiffViewer, ImpactPanel,
  VersionBadge, ProjectStateBadge,
} from '../../components/books/BooksSharedUI.jsx';

// 🔽 CHANGED: helper برای نمایش رشته در هدر
import { getField as getFieldMeta } from '../../core/education/curriculum.js';

// ================================================================
// SECTION 1: ICONS
// ================================================================

const IconBack = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 20} height={p.size || 20} fill="none" {...p}>
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconChevron = ({ dir = 'down', size = 16 }) => {
  const paths = {
    down: 'M6 9l6 6 6-6', up: 'M6 15l6-6 6 6',
    left: 'M15 6l-6 6 6 6', right: 'M9 6l6 6-6 6',
  };
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path d={paths[dir]} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};
const IconEye = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);
const IconEdit = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconCheck = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconX = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconRollback = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M3 7v6h6M3 13a9 9 0 1015-6.7L21 9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconDiff = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M12 3v18M5 8h4M15 16h4M5 16h4M15 8h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
);
const IconImpact = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
  </svg>
);
const IconHistory = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M3 3v6h6M3.51 9a9 9 0 0114.85-3.36L21 8M21 21v-6h-6M20.49 15a9 9 0 01-14.85 3.36L3 16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconVideo = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v-4z" fill="currentColor" opacity="0.35"/>
    <rect x="3" y="6" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.7"/>
  </svg>
);
const IconTarget = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.7"/>
    <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
  </svg>
);

// 🆕 آیکن سطل زباله — برای حذف کامل پروژه
const IconTrash = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" {...p}>
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ================================================================
// SECTION 2: CONSTANTS
// ================================================================

const TABS = Object.freeze({
  CONTENT: 'content',
  DIFF: 'diff',
  IMPACT: 'impact',
  HISTORY: 'history',
});

const TAB_LABELS = {
  [TABS.CONTENT]: 'محتوای نسخه',
  [TABS.DIFF]: 'تفاوت‌ها',
  [TABS.IMPACT]: 'تأثیر بر دانش‌آموزان',
  [TABS.HISTORY]: 'تاریخچه‌ی نسخه‌ها',
};

// ================================================================
// SECTION 3: MAIN ENTRY + GUARD
// ================================================================

export default function AdminProjectReview() {
  const { user } = useAuth();

  if (!user || user.role !== 'admin') {
    return <ReviewAccessDenied user={user} />;
  }

  return (
    <BooksEngineProvider user={user}>
      <ReviewRouter />
    </BooksEngineProvider>
  );
}

function ReviewAccessDenied({ user }) {
  const navigate = useNavigate();
  const target = { student: '/StudentHome', teacher: '/TeacherHome', parent: '/ParentHome' }[user?.role] || '/login';
  return (
    <div className="apr-denied">
      <div className="apr-denied-card">
        <div className="apr-denied-icon">🔐</div>
        <h2>دسترسی محدود</h2>
        <p>این صفحه مخصوص مدیر سیستم است.</p>
        <button className="apr-btn apr-btn-primary" onClick={() => navigate(target)}>بازگشت</button>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 4: ROUTER (request vs project)
// ================================================================

function ReviewRouter() {
  const params = useParams();
  const requestId = params.requestId;
  const projectId = params.projectId;

  if (projectId) return <ProjectReviewMode projectId={projectId} />;
  if (requestId) return <RequestReviewMode requestId={requestId} />;

  return (
    <div className="apr-error-page">
      <EmptyState icon="❓" title="پارامتر نامعتبر" description="نشانی صفحه معتبر نیست." />
    </div>
  );
}

// ================================================================
// SECTION 5: REQUEST REVIEW MODE
// ================================================================

function RequestReviewMode({ requestId }) {
  const navigate = useNavigate();
  const { ready } = useEngineReady();
  const request = useRequest(requestId);
  const { approve, reject, requestEdit } = useAdminRequestActions();

  const teacherProfile = useMemo(
    () => (request?.userId ? dataEngine.teacher.getByUserId(request.userId) : null),
    [request?.userId]
  );
  const teacherProjects = useMemo(
    () => (teacherProfile ? dataEngine.project.listByTeacher(teacherProfile.id) : []),
    [teacherProfile]
  );

  const [confirmType, setConfirmType] = useState(null);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState(null);   // 🆕

  const handleConfirm = useCallback(async () => {
    if (!confirmType) return;
    setActionError(null);
    const res =
      confirmType === 'approve' ? await approve.run(requestId, reason) :
      confirmType === 'reject' ? await reject.run(requestId, reason) :
      await requestEdit.run(requestId, reason);
    if (res?.ok) {
      setConfirmType(null);
      setReason('');
      if (confirmType === 'approve') navigate('/admin/books?tab=projects');
    } else {
      // 🆕 نمایش پیام خطا در صفحه
      const raw = (res?.errors || [])[0] || 'خطای نامشخص در انجام عملیات';
      const map = {
        project_exists: 'پروژه‌ی مشابه از قبل برای این معلم و پایه/کتاب وجود دارد. ابتدا پروژه‌ی قبلی را حذف کنید (از تب «همه‌ی پروژه‌ها» یا با دکمه‌ی «حذف کامل» در همین صفحه) و سپس دوباره تایید بزنید.',
        project_key_taken: 'پروژه‌ای با این ترکیب پایه/کتاب/پوشه قبلاً ثبت شده است.',
        not_found: 'درخواست یافت نشد.',
        forbidden: 'شما اجازه‌ی این عملیات را ندارید.',
        rate_limited: 'تعداد درخواست‌ها زیاد است. کمی بعد دوباره تلاش کنید.',
        invalid_grade_or_subject: 'پایه یا کتاب معتبر نیست.',
        field_required: 'انتخاب رشته برای پایه‌های ۱۰ تا ۱۲ الزامی است.',
      };
      setActionError(map[raw] || raw);
    }
  }, [confirmType, requestId, reason, approve, reject, requestEdit, navigate]);

  if (!ready) return <ReviewLoading />;

  if (!request) {
    return (
      <div className="apr-error-page">
        <EmptyState
          icon="📭"
          title="درخواست یافت نشد"
          description="این درخواست وجود ندارد یا حذف شده است."
          action={<button className="apr-btn apr-btn-primary" onClick={() => navigate('/admin/books')}>بازگشت</button>}
        />
      </div>
    );
  }

  const gradeLabel = GRADE_TITLES[String(request.grade)] || `پایه ${request.grade}`;
  const subjectLabel = SUBJECT_LABELS[request.subject] || request.subject;

  return (
    <div className="apr-page">
      <ReviewBackground />

      <header className="apr-header apr-header-request">
        <div className="apr-header-inner">
          <button className="apr-icon-btn" onClick={() => navigate('/admin/books')} title="بازگشت">
            <IconBack />
          </button>
          <div className="apr-header-title-group">
            <h1 className="apr-title">بررسی درخواست معلمی</h1>
            <div className="apr-header-meta">
              <Badge tone="warning" size="sm">⏳ در انتظار بررسی</Badge>
            </div>
          </div>
          <div />
        </div>
      </header>

      <main className="apr-main apr-main-request">
        {/* 🆕 نمایش خطای انجام عملیات */}
        {actionError && (
          <div
            role="alert"
            style={{
              marginBottom: 16,
              padding: '14px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 12,
              color: '#991b1b',
              fontSize: '0.9rem',
              lineHeight: 1.7,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>⚠️</span>
            <div>
              <strong style={{ display: 'block', marginBottom: 4 }}>خطا در انجام عملیات</strong>
              <span>{actionError}</span>
            </div>
          </div>
        )}

        {/* کارت اطلاعات درخواست */}
        <section className="apr-request-detail-card">
          <div className="apr-request-hero">
            <div className="apr-request-avatar">
              {request.formData?.avatarBlobId ? (
                <BlobImage blobId={request.formData.avatarBlobId} alt={request.formData.fullName} />
              ) : (
                <span className="apr-avatar-fallback">
                  {(request.formData?.fullName || '؟').charAt(0)}
                </span>
              )}
            </div>
            <div className="apr-request-title-group">
              <h2>{request.formData?.fullName || 'بدون نام'}</h2>
              <div className="apr-request-target">
                📚 {subjectLabel} — {gradeLabel}
              </div>
            </div>
          </div>

          <div className="apr-detail-grid">
            <DetailItem icon="🏷️" label="folderName" value={request.proposedFolderName} mono />
            <DetailItem icon="💼" label="سابقه" value={`${request.formData?.experienceYears || 0} سال`} />
            <DetailItem icon="📅" label="تاریخ ثبت" value={
              request.submittedAt ? new Date(request.submittedAt).toLocaleString('fa-IR') : '—'
            } />
            <DetailItem icon="🆔" label="شناسه" value={request.id} mono />
          </div>

          {request.formData?.specialties?.length > 0 && (
            <div className="apr-detail-section">
              <div className="apr-detail-label">⭐ تخصص‌ها</div>
              <div className="apr-chips">
                {request.formData.specialties.map((s) => <Chip key={s} tone="purple">{s}</Chip>)}
              </div>
            </div>
          )}

          {request.formData?.bio && (
            <div className="apr-detail-section">
              <div className="apr-detail-label">📝 بیوگرافی</div>
              <p className="apr-detail-text">{request.formData.bio}</p>
            </div>
          )}
        </section>

        {/* پروژه‌های قبلی معلم */}
        <section className="apr-section-card">
          <header className="apr-section-header">
            <h3>📖 پروژه‌های فعلی این معلم</h3>
            <span className="apr-count-badge">{teacherProjects.length}</span>
          </header>

          {teacherProjects.length === 0 ? (
            <div className="apr-empty-inline">
              ✨ این معلم هنوز پروژه‌ای ندارد — با تایید، اولین پروژه ساخته می‌شود.
            </div>
          ) : (
            <div className="apr-projects-list">
              {teacherProjects.map((p) => (
                <Link
                  key={p.id}
                  to={`/admin/books/review/project/${p.id}`}
                  className="apr-project-mini-card"
                >
                  <div className="apr-project-mini-cover">
                    {p.coverImageBlobId ? (
                      <BlobImage blobId={p.coverImageBlobId} alt={p.title} />
                    ) : (
                      <span className="apr-project-mini-placeholder">📘</span>
                    )}
                  </div>
                  <div className="apr-project-mini-body">
                    <div className="apr-project-mini-title">
                      {p.title || `${SUBJECT_LABELS[p.subject]} — ${GRADE_TITLES[String(p.grade)]}`}
                    </div>
                    <div className="apr-project-mini-meta">
                      <ProjectStateBadge state={p.state} size="sm" />
                    </div>
                  </div>
                  <IconChevron dir="left" size={18} />
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Action bar */}
        <ActionBar
          actions={[
            { key: 'edit', label: 'درخواست اصلاح', icon: <IconEdit size={16} />, tone: 'warning', onClick: () => setConfirmType('edit') },
            { key: 'reject', label: 'رد درخواست', icon: <IconX size={16} />, tone: 'danger', onClick: () => setConfirmType('reject') },
            { key: 'approve', label: 'تایید درخواست', icon: <IconCheck size={16} />, tone: 'success', onClick: () => setConfirmType('approve') },
          ]}
        />
      </main>

      {/* Modals */}
      <ConfirmModal
        isOpen={confirmType === 'approve'}
        onClose={() => { setConfirmType(null); setActionError(null); }}
        onConfirm={handleConfirm}
        title="تایید درخواست معلمی"
        message={`آیا از تایید درخواست ${request.formData?.fullName} برای تدریس ${subjectLabel} ${gradeLabel} مطمئن هستید؟`}
        confirmText="✓ تایید کن"
        tone="info"
        requireReason
        reasonLabel="یادداشت (اختیاری)"
        loading={approve.loading}
        extraContent={actionError ? (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 10,
              color: '#991b1b',
              fontSize: '0.85rem',
              lineHeight: 1.6,
            }}
          >
            ⚠️ {actionError}
          </div>
        ) : null}
      />
      <ConfirmModal
        isOpen={confirmType === 'reject'}
        onClose={() => { setConfirmType(null); setActionError(null); }}
        onConfirm={handleConfirm}
        title="رد درخواست"
        message="لطفاً دلیل رد را ذکر کنید. این پیام برای معلم ارسال می‌شود."
        confirmText="✕ رد کن"
        tone="danger"
        requireReason
        reasonLabel="دلیل رد"
        reasonRequired
        loading={reject.loading}
        extraContent={actionError ? (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 10,
              color: '#991b1b',
              fontSize: '0.85rem',
              lineHeight: 1.6,
            }}
          >
            ⚠️ {actionError}
          </div>
        ) : null}
      />
      <ConfirmModal
        isOpen={confirmType === 'edit'}
        onClose={() => { setConfirmType(null); setActionError(null); }}
        onConfirm={handleConfirm}
        title="درخواست اصلاح"
        message="به معلم اجازه دهید درخواست را اصلاح کند. توضیح دهید چه چیزی باید تغییر کند."
        confirmText="ارسال درخواست اصلاح"
        tone="warning"
        requireReason
        reasonLabel="چه چیزی باید اصلاح شود؟"
        reasonRequired
        loading={requestEdit.loading}
        extraContent={actionError ? (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '10px 12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: 10,
              color: '#991b1b',
              fontSize: '0.85rem',
              lineHeight: 1.6,
            }}
          >
            ⚠️ {actionError}
          </div>
        ) : null}
      />
    </div>
  );
}

function DetailItem({ icon, label, value, mono }) {
  return (
    <div className="apr-detail-item">
      <span className="apr-detail-icon">{icon}</span>
      <div className="apr-detail-body">
        <div className="apr-detail-label">{label}</div>
        <div className={`apr-detail-value ${mono ? 'mono' : ''}`}>{value}</div>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 6: PROJECT REVIEW MODE
// ================================================================

function ProjectReviewMode({ projectId }) {
  const navigate = useNavigate();
  const { user, toast } = useBooksEngine();
  const { ready } = useEngineReady();

  const project = useProject(projectId);
  const teacherProfile = useTeacherByUserId(project?.teacherId ? dataEngine.teacher.get(project.teacherId)?.userId : null);

  const versions = useProjectVersions(projectId, {});
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.CONTENT);
  const userSelectedVersionRef = useRef(false);   // 🆕

  // diff و impact
  const isUpdateReview = project?.state === PROJECT_STATE.UPDATE_PENDING;
  const isInitialReview = project?.state === PROJECT_STATE.PENDING_INITIAL;
  const isPendingReview = isUpdateReview || isInitialReview;   // 🆕

  const diff = useDiff(
    isUpdateReview ? project?.publishedVersionId : null,
    isUpdateReview ? project?.pendingVersionId : null
  );
  const impact = useImpactAnalysis(
    isUpdateReview ? projectId : null,
    isUpdateReview ? diff : null
  );

  // انتخاب پیش‌فرض نسخه
  useEffect(() => {
    if (!project) return;
    if (userSelectedVersionRef.current) return;   // 🆕 کاربر خودش انتخاب کرده
    const defaultId =
      project.pendingVersionId ||
      project.draftVersionId ||
      project.publishedVersionId ||
      versions[0]?.id;
    setSelectedVersionId(defaultId || null);
  }, [project?.id, project?.pendingVersionId, project?.draftVersionId, project?.publishedVersionId, versions]);

  const selectedVersion = useVersion(selectedVersionId);

  // actions
  const { approveAndPublish, rejectPending, requestEdit: requestEditVersion, rollbackTo } = useVersionActions();
  const { suspend, resume, hardDelete } = useProjectActions();

  const [modal, setModal] = useState(null); // 'approve' | 'reject' | 'edit' | 'rollback' | 'suspend' | 'resume' | 'harddelete'
  const [reason, setReason] = useState('');
  const [rollbackTarget, setRollbackTarget] = useState(null);
  // 🆕 state برای مودال تنظیمات اشتراک
  const [showSubSettings, setShowSubSettings] = useState(false);

  // preload edit mode state
  const [editMode, setEditMode] = useState(false);
  const [localChapters, setLocalChapters] = useState(null); // وقتی admin ویرایش می‌کند
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  useEffect(() => {
    if (selectedVersion && !hasLocalChanges) {
      setLocalChapters(JSON.parse(JSON.stringify(selectedVersion.chapters || [])));
    }
  }, [selectedVersion?.id, selectedVersion?.contentHash]);






  // shortcuts
  useKeyboardShortcuts({
    'escape': () => {
      if (modal) setModal(null);
      else if (editMode && !hasLocalChanges) setEditMode(false);
    },
  });

  // 🆕 این useMemo باید قبل از هر return شرطی باشد
  const tabs = useMemo(() => {
    const base = [TABS.CONTENT];
    if (isUpdateReview) base.push(TABS.DIFF, TABS.IMPACT);
    base.push(TABS.HISTORY);
    return base;
  }, [isUpdateReview]);

  if (!ready) return <ReviewLoading />;

  if (!project) {
    return (
      <div className="apr-error-page">
        <EmptyState
          icon="📦"
          title="پروژه یافت نشد"
          description="این پروژه وجود ندارد یا حذف شده است."
          action={<button className="apr-btn apr-btn-primary" onClick={() => navigate('/admin/books')}>بازگشت</button>}
        />
      </div>
    );
  }

  const gradeLabel = GRADE_TITLES[String(project.grade)] || `پایه ${project.grade}`;
  const subjectLabel = SUBJECT_LABELS[project.subject] || project.subject;
  // 🔽 CHANGED: نمایش کامل با رشته
  const fieldMeta = project.field ? getFieldMeta(project.field) : null;
  const fullGradeLabel = fieldMeta
    ? `${gradeLabel} (${fieldMeta.shortTitle})`
    : gradeLabel;

  // ---- Action handlers ----

  const handleApprove = async () => {
    const res = await approveAndPublish.run(projectId, reason);
    if (res?.ok) {
      toast?.('نسخه با موفقیت منتشر شد', 'success');
      setModal(null);
      setReason('');
    }
  };

  const handleReject = async () => {
    const res = await rejectPending.run(projectId, reason);
    if (res?.ok) {
      toast?.('نسخه رد شد', 'success');
      setModal(null);
      setReason('');
    }
  };

  const handleRequestEdit = async () => {
    const res = await requestEditVersion.run(projectId, reason);
    if (res?.ok) {
      toast?.('درخواست اصلاح ارسال شد', 'success');
      setModal(null);
      setReason('');
    }
  };

  const handleRollback = async () => {
    if (!rollbackTarget) return;
    const res = await rollbackTo.run(projectId, rollbackTarget, reason);
    if (res?.ok) {
      toast?.('نسخه بازگردانی شد', 'success');
      setModal(null);
      setReason('');
      setRollbackTarget(null);
    }
  };

  const handleSuspend = async () => {
    const res = await suspend.run(projectId);
    if (res?.ok) {
      toast?.('پروژه معلق شد', 'success');
      setModal(null);
    }
  };

  const handleResume = async () => {
    const res = await resume.run(projectId);
    if (res?.ok) {
      toast?.('پروژه فعال شد', 'success');
      setModal(null);
    }
  };

  // 🆕 حذف کامل و دائمی پروژه — fire-and-forget تا صفحه بدون فلش ناوبری شود
  const handleHardDelete = () => {
    setModal(null);
    navigate('/admin/books?tab=projects', { replace: true });
    hardDelete.run(projectId);
  };

  const handleSaveAdminEdit = () => {
    if (!selectedVersion || !localChapters) return;
    if (selectedVersion.status === VERSION_STATUS.PUBLISHED) {
      toast?.('نسخه‌ی منتشرشده قابل ویرایش نیست', 'error');
      return;
    }
    const res = dataEngine.version.updateChapters(selectedVersion.id, localChapters);
    if (res.ok) {
      auditLog.record({
        actorId: user?.id, actorRole: user?.role,
        action: 'admin.version.inline_edit',
        targetType: 'version', targetId: selectedVersion.id,
        after: { chapterCount: localChapters.length },
      });
      toast?.('تغییرات ذخیره شد', 'success');
      setEditMode(false);
      setHasLocalChanges(false);
    } else {
      toast?.('خطا در ذخیره', 'error');
    }
  };

  const canEditContent =
    !project.archivedAt &&
    selectedVersion &&
    selectedVersion.status !== VERSION_STATUS.PUBLISHED &&
    selectedVersion.status !== VERSION_STATUS.SUPERSEDED;



  return (
    <div className="apr-page">
      <ReviewBackground />

      {/* Header */}
      <header className="apr-header">
        <div className="apr-header-inner">
          <button className="apr-icon-btn" onClick={() => navigate('/admin/books')} title="بازگشت">
            <IconBack />
          </button>

          <div className="apr-header-title-group">
            <h1 className="apr-title">
              {/* 🔽 CHANGED: با رشته */}
              {project.title || `${subjectLabel} — ${fullGradeLabel}`}
            </h1>
            <div className="apr-header-meta">
              <ProjectStateBadge state={project.state} size="sm" />
              {/* 🔽 CHANGED: نمایش رشته به‌عنوان badge */}
              {fieldMeta && (
                <Badge tone="purple" size="sm">🎓 {fieldMeta.title}</Badge>
              )}
              {teacherProfile && (
                <span className="apr-header-teacher">
                  👤 {teacherProfile.fullName || 'معلم'}
                </span>
              )}
              <span className="apr-header-folder">🏷️ {project.folderName}</span>
            </div>
          </div>

          <div className="apr-header-actions">
            <VersionSelector
              versions={versions}
              selectedId={selectedVersionId}
              project={project}
              onSelect={(id) => {
                if (hasLocalChanges) {
                  if (!window.confirm('تغییرات ذخیره‌نشده از بین می‌رود. ادامه؟')) return;
                }
                userSelectedVersionRef.current = true;   // 🆕
                setSelectedVersionId(id);
                setHasLocalChanges(false);
                setEditMode(false);
              }}
            />
          </div>
        </div>

        {/* Tabs */}
        <nav className="apr-tabs" role="tablist">
          {tabs.map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              className={`apr-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {key === TABS.CONTENT && <IconEye size={14} />}
              {key === TABS.DIFF && <IconDiff size={14} />}
              {key === TABS.IMPACT && <IconImpact size={14} />}
              {key === TABS.HISTORY && <IconHistory size={14} />}
              <span>{TAB_LABELS[key]}</span>
            </button>
          ))}
        </nav>
      </header>

      {/* Banner for update */}
      {isUpdateReview && (
        <div className="apr-update-banner">
          <div className="apr-update-banner-icon">🔄</div>
          <div className="apr-update-banner-text">
            معلم درخواست بروزرسانی داده است. نسخه‌ی فعال همچنان برای دانش‌آموزان نمایش داده می‌شود.
          </div>
          <div className="apr-update-banner-diff">
            {diff && (
              <>
                <span className="diff-stat add">➕{diff.summary.addedCount}</span>
                <span className="diff-stat mod">✏️{diff.summary.modifiedCount}</span>
                <span className="diff-stat del">🗑{diff.summary.removedCount}</span>
                <span className="diff-stat mov">↔{diff.summary.movedCount}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* 🆕 بنر یادداشت معلم برای مدیر */}
      {selectedVersion?.teacherNote && (
        <div
          className="apr-update-banner"
          style={{
            background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
            borderColor: '#7dd3fc',
            marginTop: 16,
          }}
        >
          <div className="apr-update-banner-icon">📝</div>
          <div className="apr-update-banner-text" style={{ color: '#0369a1' }}>
            <strong>یادداشت معلم برای این نسخه:</strong> {selectedVersion.teacherNote}
          </div>
        </div>
      )}

      {/* Edit toolbar */}
      {activeTab === TABS.CONTENT && canEditContent && (
        <div className="apr-edit-toolbar">
          {!editMode ? (
            <button className="apr-btn apr-btn-ghost" onClick={() => setEditMode(true)}>
              <IconEdit size={14} /> ویرایش به نمایندگی معلم
            </button>
          ) : (
            <>
              <span className="apr-edit-indicator">
                ✏️ در حالت ویرایش — تغییرات پس از ذخیره در audit log ثبت می‌شود
              </span>
              <div className="apr-btn-group">
                <button
                  className="apr-btn apr-btn-ghost"
                  onClick={() => {
                    if (hasLocalChanges && !window.confirm('تغییرات ذخیره‌نشده از بین می‌رود. ادامه؟')) return;
                    setLocalChapters(JSON.parse(JSON.stringify(selectedVersion.chapters || [])));
                    setEditMode(false);
                    setHasLocalChanges(false);
                  }}
                >
                  انصراف
                </button>
                <button
                  className="apr-btn apr-btn-primary"
                  onClick={handleSaveAdminEdit}
                  disabled={!hasLocalChanges}
                >
                  <IconCheck size={14} /> ذخیره
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Main content */}
      <main className="apr-main">
        {activeTab === TABS.CONTENT && (
          <ContentTab
            version={selectedVersion}
            chapters={localChapters || selectedVersion?.chapters || []}
            editMode={editMode}
            onChaptersChange={(next) => {
              setLocalChapters(next);
              setHasLocalChanges(true);
            }}
            projectId={projectId}
            project={project}
          />
        )}

        {activeTab === TABS.DIFF && (
          <DiffTab diff={diff} />
        )}

        {activeTab === TABS.IMPACT && (
          <ImpactTab impact={impact} project={project} diff={diff} />
        )}

        {activeTab === TABS.HISTORY && (
          <HistoryTab
            versions={versions}
            project={project}
            onRollback={(versionId) => {
              setRollbackTarget(versionId);
              setModal('rollback');
            }}
            onSelectVersion={(id) => setSelectedVersionId(id)}
          />
        )}
      </main>

      {/* Action bar */}
      <ActionBar
        actions={[
          project.state === PROJECT_STATE.SUSPENDED && {
            key: 'resume',
            label: 'فعال‌سازی',
            icon: <IconCheck size={16} />,
            tone: 'success',
            onClick: () => setModal('resume'),
          },
          project.state !== PROJECT_STATE.SUSPENDED && project.publishedVersionId && {
            key: 'suspend',
            label: 'تعلیق',
            icon: <IconX size={16} />,
            tone: 'danger',
            onClick: () => setModal('suspend'),
          },
          // 🆕 هر دو حالت: بررسی اولیه و بروزرسانی
          isPendingReview && {
            key: 'edit',
            label: isInitialReview ? 'درخواست اصلاح' : 'درخواست اصلاح نسخه',
            icon: <IconEdit size={16} />,
            tone: 'warning',
            onClick: () => setModal('edit'),
          },
          isPendingReview && {
            key: 'reject',
            label: isInitialReview ? 'رد انتشار' : 'رد بروزرسانی',
            icon: <IconX size={16} />,
            tone: 'danger',
            onClick: () => setModal('reject'),
          },
          isPendingReview && {
            key: 'approve',
            label: isInitialReview ? 'تایید و انتشار' : 'تایید و انتشار',
            icon: <IconCheck size={16} />,
            tone: 'success',
            onClick: () => setModal('approve'),
          },
          // 🆕 دکمه تنظیمات اشتراک
          !project.archivedAt && {
            key: 'subscription',
            label: 'تنظیمات اشتراک',
            icon: <span style={{ fontSize: 16 }}>💰</span>,
            tone: 'ghost',
            onClick: () => setShowSubSettings(true),
          },
          // 🆕 حذف کامل و دائمی — همیشه در دسترس (به‌جز پروژه‌های آرشیو‌شده)
          !project.archivedAt && {
            key: 'harddelete',
            label: 'حذف کامل',
            icon: <IconTrash size={16} />,
            tone: 'danger',
            onClick: () => setModal('harddelete'),
          },
        ].filter(Boolean)}
      />

      {/* Modals */}
      <ConfirmModal
        isOpen={modal === 'approve'}
        onClose={() => setModal(null)}
        onConfirm={handleApprove}
        title="تایید و انتشار نسخه"
        message={
          impact?.data?.summary?.studentsLosingProgress > 0
            ? `با تایید، نسخه‌ی جدید برای دانش‌آموزان فعال می‌شود. توجه: ${impact.data.summary.studentsLosingProgress} دانش‌آموز پیشرفت‌شان در برخی مراحل آرشیو می‌شود (پاک نمی‌شود).`
            : 'با تایید، نسخه‌ی جدید برای دانش‌آموزان فعال می‌شود و نسخه‌ی قبلی جایگزین می‌گردد.'
        }
        confirmText="✓ تایید و انتشار"
        tone="info"
        requireReason
        reasonLabel="یادداشت (اختیاری)"
        loading={approveAndPublish.loading}
      />

      <ConfirmModal
        isOpen={modal === 'reject'}
        onClose={() => setModal(null)}
        onConfirm={handleReject}
        title="رد بروزرسانی"
        message="لطفاً دلیل رد را ذکر کنید تا معلم بتواند اصلاح کند."
        confirmText="✕ رد کن"
        tone="danger"
        requireReason
        reasonLabel="دلیل رد"
        reasonRequired
        loading={rejectPending.loading}
      />

      <ConfirmModal
        isOpen={modal === 'edit'}
        onClose={() => setModal(null)}
        onConfirm={handleRequestEdit}
        title="درخواست اصلاح نسخه"
        message="به معلم اجازه می‌دهید نسخه را اصلاح کند؟"
        confirmText="ارسال درخواست اصلاح"
        tone="warning"
        requireReason
        reasonLabel="چه چیزی باید اصلاح شود؟"
        reasonRequired
        loading={requestEditVersion.loading}
      />

      <ConfirmModal
        isOpen={modal === 'rollback'}
        onClose={() => { setModal(null); setRollbackTarget(null); }}
        onConfirm={handleRollback}
        title="بازگردانی به نسخه‌ی قبلی"
        message={`آیا از بازگردانی به این نسخه مطمئن هستید؟ یک draft جدید بر اساس این نسخه ساخته می‌شود.`}
        confirmText="↩️ بازگردانی"
        tone="warning"
        requireReason
        reasonLabel="یادداشت (اختیاری)"
        loading={rollbackTo.loading}
      />

      <ConfirmModal
        isOpen={modal === 'suspend'}
        onClose={() => setModal(null)}
        onConfirm={handleSuspend}
        title="تعلیق پروژه"
        message="با تعلیق، پروژه از دسترس دانش‌آموزان خارج می‌شود (فقط برای مدت موقت). می‌توانید بعداً فعال کنید."
        confirmText="تعلیق پروژه"
        tone="danger"
        loading={suspend.loading}
      />

      <ConfirmModal
        isOpen={modal === 'resume'}
        onClose={() => setModal(null)}
        onConfirm={handleResume}
        title="فعال‌سازی پروژه"
        message="آیا از فعال‌سازی مجدد این پروژه مطمئن هستید؟"
        confirmText="فعال کن"
        tone="info"
        loading={resume.loading}
      />

      {/* 🆕 حذف کامل و دائمی پروژه */}
      <ConfirmModal
        isOpen={modal === 'harddelete'}
        onClose={() => setModal(null)}
        onConfirm={handleHardDelete}
        title="🗑️ حذف کامل و دائمی پروژه"
        message={`آیا از حذف کامل و دائمی پروژه «${project.title || 'بدون عنوان'}» مطمئن هستید؟ این عملیات قابل بازگشت نیست. تمام فصل‌ها، درس‌ها، مراحل، آزمون‌ها، ویدیوها و تصاویر این پروژه برای همیشه حذف می‌شوند.`}
        confirmText="🗑️ حذف دائمی"
        tone="danger"
        loading={hardDelete.loading}
        extraContent={
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '12px 14px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 10,
              color: '#7f1d1d',
              fontSize: '0.85rem',
              lineHeight: 1.7,
            }}
          >
            <div style={{ marginBottom: 6, fontWeight: 700 }}>⚠️ هشدار جدی</div>
            <div>• این پروژه {project.versions?.length || 0} نسخه دارد که همه حذف می‌شوند.</div>
            {(project.stats?.studentsCount || 0) > 0 && (
              <div>• {project.stats.studentsCount} دانش‌آموز در این پروژه ثبت‌نام کرده‌اند (آمار حذف می‌شود).</div>
            )}
            <div>• این عمل قابل بازگشت نیست. توصیه: به‌جای حذف، از «تعلیق» استفاده کنید اگر می‌خواهید بعداً برگردانید.</div>
          </div>
        }
      />
      {/* 🆕 مودال تنظیمات اشتراک برای مدیر */}
      {showSubSettings && (
        <AdminSubscriptionSettingsModal
          project={project}
          onClose={() => setShowSubSettings(false)}
          onSave={(data) => {
            const res = dataEngine.project.update(projectId, data);
            if (res.ok) {
              toast?.('تنظیمات اشتراک توسط مدیر به‌روزرسانی شد', 'success');
              setShowSubSettings(false);
            } else {
              toast?.('خطا در ذخیره تنظیمات', 'error');
            }
          }}
        />
      )}
    </div>
  );
}

// ================================================================
// SECTION 7: VERSION SELECTOR
// ================================================================

function VersionSelector({ versions, selectedId, project, onSelect }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => versions.find((v) => v.id === selectedId),
    [versions, selectedId]
  );

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!versions || versions.length === 0) return null;

  return (
    <div className="apr-version-selector" ref={wrapRef}>
      <button
        type="button"
        className="apr-version-btn"
        onClick={() => setOpen((o) => !o)}
      >
        {selected && <VersionBadge status={selected.status} size="sm" />}
        <span className="apr-version-btn-label">
          نسخه {selected ? new Date(selected.createdAt).toLocaleDateString('fa-IR') : '—'}
        </span>
        <IconChevron dir={open ? 'up' : 'down'} size={14} />
      </button>

      {open && (
        <div className="apr-version-menu">
          {versions.map((v) => (
            <button
              key={v.id}
              className={`apr-version-item ${v.id === selectedId ? 'active' : ''}`}
              onClick={() => { onSelect?.(v.id); setOpen(false); }}
            >
              <VersionBadge status={v.status} size="sm" />
              <div className="apr-version-item-body">
                <span className="apr-version-item-date">
                  {new Date(v.createdAt).toLocaleDateString('fa-IR')}
                </span>
                {v.id === project?.publishedVersionId && (
                  <span className="apr-version-item-tag">فعال</span>
                )}
                {v.id === project?.pendingVersionId && (
                  <span className="apr-version-item-tag warn">در انتظار</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 8: CONTENT TAB
// ================================================================

function ContentTab({ version, chapters, editMode, onChaptersChange, projectId, project }) {
  const [openChapters, setOpenChapters] = useState({});

  useEffect(() => {
    const init = {};
    (chapters || []).forEach((ch) => { init[ch._id] = true; });
    setOpenChapters((prev) => {
      const merged = { ...init };
      for (const k of Object.keys(prev)) if (k in init) merged[k] = prev[k];
      return merged;
    });
  }, [chapters?.length]);

  const toggleChapter = (id) => setOpenChapters((p) => ({ ...p, [id]: !p[id] }));

  // --- Operations in edit mode ---

  const addChapter = () => {
    const next = [...chapters, {
      _id: `ch_${Date.now().toString(36)}`,
      title: 'فصل جدید',
      description: '',
      order: chapters.length,
      lessons: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }];
    onChaptersChange(next);
  };

  const updateChapter = (chapterId, patch) => {
    const next = chapters.map((ch) =>
      ch._id === chapterId ? { ...ch, ...patch, updatedAt: Date.now() } : ch
    );
    onChaptersChange(next);
  };

  const removeChapter = (chapterId) => {
    if (!window.confirm('این فصل و همه‌ی درس‌هایش حذف می‌شود. مطمئن هستید؟')) return;
    onChaptersChange(chapters.filter((ch) => ch._id !== chapterId));
  };

  const addLesson = (chapterId) => {
    const next = chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      const newLesson = {
        _id: `ls_${Date.now().toString(36)}`,
        title: 'درس جدید',
        description: '',
        imageBlobId: null,
        order: ch.lessons.length,
        steps: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      return { ...ch, lessons: [...ch.lessons, newLesson], updatedAt: Date.now() };
    });
    onChaptersChange(next);
  };

  const updateLesson = (chapterId, lessonId, patch) => {
    const next = chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) =>
          ls._id === lessonId ? { ...ls, ...patch, updatedAt: Date.now() } : ls
        ),
      };
    });
    onChaptersChange(next);
  };

  const removeLesson = (chapterId, lessonId) => {
    if (!window.confirm('این درس حذف شود؟')) return;
    const next = chapters.map((ch) =>
      ch._id !== chapterId ? ch : { ...ch, lessons: ch.lessons.filter((l) => l._id !== lessonId) }
    );
    onChaptersChange(next);
  };

  const addStep = (chapterId, lessonId) => {
    const next = chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          const newStep = {
            _id: `st_${Date.now().toString(36)}`,
            title: 'مرحله جدید',
            description: '',
            durationMinutes: 0,
            order: ls.steps.length,
            videos: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          return { ...ls, steps: [...ls.steps, newStep], updatedAt: Date.now() };
        }),
      };
    });
    onChaptersChange(next);
  };

  const updateStep = (chapterId, lessonId, stepId, patch) => {
    const next = chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          return {
            ...ls,
            steps: ls.steps.map((st) =>
              st._id === stepId ? { ...st, ...patch, updatedAt: Date.now() } : st
            ),
          };
        }),
      };
    });
    onChaptersChange(next);
  };

  const removeStep = (chapterId, lessonId, stepId) => {
    if (!window.confirm('این مرحله حذف شود؟')) return;
    const next = chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          return { ...ls, steps: ls.steps.filter((s) => s._id !== stepId) };
        }),
      };
    });
    onChaptersChange(next);
  };

  if (!version) {
    return (
      <div className="apr-content-empty">
        <EmptyState icon="📄" title="نسخه‌ای برای نمایش وجود ندارد" />
      </div>
    );
  }

  return (
    <div className="apr-content">
      {chapters.length === 0 ? (
        <EmptyState
          icon="📚"
          title="این نسخه هنوز محتوایی ندارد"
          description={editMode ? 'با افزودن فصل شروع کنید.' : 'معلم هنوز محتوایی نساخته است.'}
          action={editMode && (
            <button className="apr-btn apr-btn-primary" onClick={addChapter}>
              ➕ افزودن فصل
            </button>
          )}
        />
      ) : (
        <div className="apr-chapters">
          {chapters.map((ch, chIdx) => (
            <AdminChapterCard
              key={ch._id}
              chapter={ch}
              chapterIndex={chIdx}
              chapterCount={chapters.length}
              isOpen={!!openChapters[ch._id]}
              onToggle={() => toggleChapter(ch._id)}
              editMode={editMode}
              onRename={(title) => updateChapter(ch._id, { title })}
              onDelete={() => removeChapter(ch._id)}
              onAddLesson={() => addLesson(ch._id)}
              onUpdateLesson={(lessonId, patch) => updateLesson(ch._id, lessonId, patch)}
              onDeleteLesson={(lessonId) => removeLesson(ch._id, lessonId)}
              onAddStep={(lessonId) => addStep(ch._id, lessonId)}
              onUpdateStep={(lessonId, stepId, patch) => updateStep(ch._id, lessonId, stepId, patch)}
              onDeleteStep={(lessonId, stepId) => removeStep(ch._id, lessonId, stepId)}
            />
          ))}

          {editMode && (
            <button className="apr-add-chapter" onClick={addChapter}>
              ➕ افزودن فصل جدید
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 9: ADMIN CHAPTER CARD
// ================================================================

function AdminChapterCard({
  chapter, chapterIndex, chapterCount, isOpen, onToggle, editMode,
  onRename, onDelete, onAddLesson, onUpdateLesson, onDeleteLesson,
  onAddStep, onUpdateStep, onDeleteStep,
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(chapter.title);
  const inputRef = useRef(null);

  useEffect(() => { setTitleDraft(chapter.title); }, [chapter.title]);
  useEffect(() => {
    if (editingTitle && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editingTitle]);

  const commitTitle = () => {
    const t = titleDraft.trim();
    if (t && t !== chapter.title) onRename(t);
    else setTitleDraft(chapter.title);
    setEditingTitle(false);
  };

  const lessonsCount = chapter.lessons?.length || 0;
  const stepsCount = (chapter.lessons || []).reduce((s, l) => s + (l.steps?.length || 0), 0);

  return (
    <section className={`apr-chapter ${isOpen ? 'open' : ''}`}>
      <header className="apr-chapter-header">
        <button className="apr-chapter-toggle" onClick={onToggle} aria-expanded={isOpen}>
          <span className="apr-chapter-arrow">
            <IconChevron dir={isOpen ? 'up' : 'down'} size={18} />
          </span>
          <span className="apr-chapter-num">{chapterIndex + 1}</span>
          {editingTitle && editMode ? (
            <input
              ref={inputRef}
              className="apr-chapter-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
                if (e.key === 'Escape') { setTitleDraft(chapter.title); setEditingTitle(false); }
              }}
              onClick={(e) => e.stopPropagation()}
              maxLength={200}
            />
          ) : (
            <span className="apr-chapter-title">{chapter.title}</span>
          )}
          <div className="apr-chapter-stats">
            <span className="apr-chapter-stat">
              <IconTarget size={12} /> {lessonsCount} درس
            </span>
            {stepsCount > 0 && (
              <span className="apr-chapter-stat">
                <IconVideo size={12} /> {stepsCount} مرحله
              </span>
            )}
          </div>
        </button>

        {editMode && (
          <div className="apr-chapter-actions">
            <button className="apr-icon-btn apr-icon-btn-sm" onClick={() => setEditingTitle(true)} title="ویرایش عنوان">
              <IconEdit size={14} />
            </button>
            <button className="apr-icon-btn apr-icon-btn-sm apr-icon-btn-danger" onClick={onDelete} title="حذف">
              <IconX size={14} />
            </button>
          </div>
        )}
      </header>

      {isOpen && (
        <div className="apr-chapter-body">
          {lessonsCount === 0 ? (
            <div className="apr-empty-lessons">
              <span>این فصل درسی ندارد.</span>
              {editMode && (
                <button className="apr-btn apr-btn-ghost" onClick={onAddLesson}>
                  ➕ افزودن درس
                </button>
              )}
            </div>
          ) : (
            <div className="apr-lessons">
              {chapter.lessons.map((ls, idx) => (
                <AdminLessonRow
                  key={ls._id}
                  lesson={ls}
                  lessonIndex={idx}
                  editMode={editMode}
                  onUpdate={(patch) => onUpdateLesson(ls._id, patch)}
                  onDelete={() => onDeleteLesson(ls._id)}
                  onAddStep={() => onAddStep(ls._id)}
                  onUpdateStep={(stepId, patch) => onUpdateStep(ls._id, stepId, patch)}
                  onDeleteStep={(stepId) => onDeleteStep(ls._id, stepId)}
                />
              ))}
              {editMode && (
                <button className="apr-add-lesson" onClick={onAddLesson}>
                  ➕ افزودن درس
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ================================================================
// SECTION 10: ADMIN LESSON ROW
// ================================================================

function AdminLessonRow({
  lesson, lessonIndex, editMode,
  onUpdate, onDelete, onAddStep, onUpdateStep, onDeleteStep,
}) {
  const [open, setOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(lesson.title);
  const inputRef = useRef(null);

  useEffect(() => { setTitleDraft(lesson.title); }, [lesson.title]);
  useEffect(() => {
    if (editingTitle && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editingTitle]);

  const commitTitle = () => {
    const t = titleDraft.trim();
    if (t && t !== lesson.title) onUpdate({ title: t });
    else setTitleDraft(lesson.title);
    setEditingTitle(false);
  };

  const stepsCount = lesson.steps?.length || 0;
  const videosCount = (lesson.steps || []).reduce((s, st) => s + (st.videos?.length || 0), 0);

  return (
    <div className={`apr-lesson ${open ? 'open' : ''}`}>
      <header className="apr-lesson-header">
        <button className="apr-lesson-toggle" onClick={() => setOpen((o) => !o)}>
          <IconChevron dir={open ? 'up' : 'down'} size={16} />
          <div className="apr-lesson-thumb">
            {lesson.imageBlobId ? (
              <BlobImage blobId={lesson.imageBlobId} alt={lesson.title} />
            ) : (
              <span className="apr-lesson-thumb-ph">📘</span>
            )}
          </div>
          <div className="apr-lesson-title-group">
            <span className="apr-lesson-index">درس {lessonIndex + 1}</span>
            {editingTitle && editMode ? (
              <input
                ref={inputRef}
                className="apr-lesson-title-input"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
                  if (e.key === 'Escape') { setTitleDraft(lesson.title); setEditingTitle(false); }
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="apr-lesson-title">{lesson.title}</span>
            )}
          </div>
          <div className="apr-lesson-stats">
            <span className="apr-lesson-stat">🎯 {stepsCount}</span>
            {videosCount > 0 && <span className="apr-lesson-stat">🎬 {videosCount}</span>}
          </div>
        </button>

        {editMode && (
          <div className="apr-lesson-actions">
            <button className="apr-icon-btn apr-icon-btn-sm" onClick={() => setEditingTitle(true)} title="ویرایش">
              <IconEdit size={14} />
            </button>
            <button className="apr-icon-btn apr-icon-btn-sm apr-icon-btn-danger" onClick={onDelete} title="حذف">
              <IconX size={14} />
            </button>
          </div>
        )}
      </header>

      {open && (
        <div className="apr-lesson-body">
          {lesson.description && (
            <p className="apr-lesson-desc">{lesson.description}</p>
          )}

          {stepsCount === 0 ? (
            <div className="apr-empty-steps">
              این درس مرحله‌ای ندارد.
              {editMode && (
                <button className="apr-btn apr-btn-ghost" onClick={onAddStep}>
                  ➕ افزودن مرحله
                </button>
              )}
            </div>
          ) : (
            <div className="apr-steps">
              {lesson.steps.map((st, idx) => (
                <AdminStepRow
                  key={st._id}
                  step={st}
                  index={idx}
                  editMode={editMode}
                  onUpdate={(patch) => onUpdateStep(st._id, patch)}
                  onDelete={() => onDeleteStep(st._id)}
                />
              ))}
              {editMode && (
                <button className="apr-add-step" onClick={onAddStep}>
                  ➕ افزودن مرحله
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 11: ADMIN STEP ROW
// ================================================================

function AdminStepRow({ step, index, editMode, onUpdate, onDelete }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(step.title);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(step.description || '');

  useEffect(() => { setTitleDraft(step.title); }, [step.title]);
  useEffect(() => { setDescDraft(step.description || ''); }, [step.description]);

  const commitTitle = () => {
    const t = titleDraft.trim();
    if (t && t !== step.title) onUpdate({ title: t });
    setEditingTitle(false);
  };
  const commitDesc = () => {
    if (descDraft !== (step.description || '')) onUpdate({ description: descDraft });
    setEditingDesc(false);
  };

  return (
    <div className="apr-step">
      <div className="apr-step-head">
        <span className="apr-step-num">{index + 1}</span>
        <div className="apr-step-title-group">
          {editingTitle && editMode ? (
            <input
              autoFocus
              className="apr-step-title-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
                if (e.key === 'Escape') { setTitleDraft(step.title); setEditingTitle(false); }
              }}
            />
          ) : (
            <span
              className="apr-step-title"
              onClick={editMode ? () => setEditingTitle(true) : null}
            >
              {step.title}
            </span>
          )}
        </div>
        {step.durationMinutes > 0 && (
          <span className="apr-step-duration">⏱️ {step.durationMinutes} دقیقه</span>
        )}
        {editMode && (
          <button className="apr-icon-btn apr-icon-btn-sm apr-icon-btn-danger" onClick={onDelete}>
            <IconX size={14} />
          </button>
        )}
      </div>

      {(step.description || editMode) && (
        <div className="apr-step-desc-wrap">
          {editingDesc && editMode ? (
            <textarea
              autoFocus
              className="apr-step-desc-input"
              rows={3}
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={commitDesc}
              maxLength={5000}
            />
          ) : (
            <p
              className="apr-step-desc"
              onClick={editMode ? () => setEditingDesc(true) : null}
            >
              {step.description || (editMode && <em>بدون توضیح — کلیک کنید</em>)}
            </p>
          )}
        </div>
      )}

      {step.videos?.length > 0 && (
        <div className="apr-step-videos">
          {step.videos.map((v) => (
            <div key={v._id} className="apr-step-video">
              <VideoPreview
                blobId={v.blobId}
                externalUrl={v.externalUrl}
                title={v.title}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 12: DIFF TAB
// ================================================================

function DiffTab({ diff }) {
  if (!diff) {
    return (
      <div className="apr-tab-content">
        <EmptyState icon="📊" title="تفاوتی برای نمایش نیست" />
      </div>
    );
  }
  return (
    <div className="apr-tab-content">
      <div className="apr-diff-header">
        <h3>مقایسه‌ی نسخه‌ی منتشرشده با نسخه‌ی جدید</h3>
        <p className="apr-diff-sub">این تغییرات پس از تایید برای دانش‌آموزان اعمال می‌شود.</p>
      </div>
      <DiffViewer diff={diff} />
    </div>
  );
}

// ================================================================
// SECTION 13: IMPACT TAB
// ================================================================

function ImpactTab({ impact, project, diff }) {
  if (!diff) {
    return (
      <div className="apr-tab-content">
        <EmptyState icon="👥" title="اطلاعات کافی برای تحلیل نیست" />
      </div>
    );
  }
  return (
    <div className="apr-tab-content">
      <div className="apr-impact-header">
        <h3>تحلیل تأثیر بر دانش‌آموزان</h3>
        <p className="apr-impact-sub">
          پیش از تایید، تأثیر این تغییرات را بر دانش‌آموزان بررسی کنید.
        </p>
      </div>
      <ImpactPanel impact={impact} />

      {impact?.data?.perStudent?.length > 0 && (
        <div className="apr-impact-students">
          <h4>نمونه‌ی دانش‌آموزان تحت تأثیر</h4>
          <div className="apr-impact-students-list">
            {impact.data.perStudent.slice(0, 20).map((s) => (
              <div key={s.studentId} className="apr-impact-student-row">
                <span className="apr-impact-student-id mono">
                  {s.studentId.slice(0, 12)}…
                </span>
                <span className="apr-impact-student-stat">
                  📉 {s.removedCompletedSteps.length} مرحله از دست می‌رود
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================
// SECTION 14: HISTORY TAB
// ================================================================

function HistoryTab({ versions, project, onRollback, onSelectVersion }) {
  const sorted = useMemo(
    () => [...versions].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
    [versions]
  );

  if (sorted.length === 0) {
    return (
      <div className="apr-tab-content">
        <EmptyState icon="📅" title="تاریخچه‌ای وجود ندارد" />
      </div>
    );
  }

  return (
    <div className="apr-tab-content">
      <div className="apr-history-header">
        <h3>تاریخچه‌ی نسخه‌ها ({sorted.length})</h3>
      </div>

      <div className="apr-timeline">
        {sorted.map((v, idx) => {
          const isPublished = v.id === project.publishedVersionId;
          const isPending = v.id === project.pendingVersionId;
          const canRollback = !isPublished && v.status !== VERSION_STATUS.PENDING;

          return (
            <div key={v.id} className="apr-timeline-item">
              <div className="apr-timeline-marker">
                <span className={`apr-timeline-dot ${isPublished ? 'published' : isPending ? 'pending' : ''}`} />
                {idx < sorted.length - 1 && <span className="apr-timeline-line" />}
              </div>
              <div className="apr-timeline-card">
                <div className="apr-timeline-header">
                  <VersionBadge status={v.status} size="sm" />
                  <span className="apr-timeline-date">
                    {new Date(v.createdAt).toLocaleString('fa-IR')}
                  </span>
                </div>
                <div className="apr-timeline-meta">
                  <span>📚 {v.chapters?.length || 0} فصل</span>
                  <span>🎯 {(v.chapters || []).reduce((s, c) => s + (c.lessons || []).length, 0)} درس</span>
                  {v.contentHash && <span className="mono">#{v.contentHash.slice(0, 8)}</span>}
                </div>
                {v.changelog?.length > 0 && (
                  <div className="apr-timeline-changelog">
                    {v.changelog.map((c, i) => (
                      <span key={i} className="apr-timeline-changelog-item">
                        {c.type || 'change'}
                      </span>
                    ))}
                  </div>
                )}
                {v.rejectionReason && (
                  <div className="apr-timeline-reason">
                    <strong>رد:</strong> {v.rejectionReason}
                  </div>
                )}
                <div className="apr-timeline-actions">
                  <button
                    className="apr-btn apr-btn-ghost apr-btn-sm"
                    onClick={() => onSelectVersion?.(v.id)}
                  >
                    <IconEye size={14} /> مشاهده
                  </button>
                  {canRollback && (
                    <button
                      className="apr-btn apr-btn-ghost apr-btn-sm"
                      onClick={() => onRollback?.(v.id)}
                    >
                      <IconRollback size={14} /> بازگردانی
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ================================================================
// SECTION 15: ACTION BAR
// ================================================================

function ActionBar({ actions = [] }) {
  if (actions.length === 0) return null;

  return (
    <div className="apr-action-bar">
      <div className="apr-action-bar-inner">
        {actions.map((a) => (
          <button
            key={a.key}
            className={`apr-btn apr-btn-${a.tone || 'ghost'}`}
            onClick={a.onClick}
            disabled={a.disabled}
          >
            {a.icon}
            <span>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ================================================================
// SECTION 16: BACKGROUND + LOADING
// ================================================================

function ReviewBackground() {
  return (
    <div className="apr-bg" aria-hidden="true">
      <div className="apr-bg-orb apr-bg-orb-1" />
      <div className="apr-bg-orb apr-bg-orb-2" />
      <div className="apr-bg-grid" />
    </div>
  );
}

function ReviewLoading() {
  return (
    <div className="apr-page">
      <ReviewBackground />
      <header className="apr-header">
        <div className="apr-header-inner">
          <SkeletonLoader variant="line" />
        </div>
      </header>
      <main className="apr-main">
        <SkeletonLoader variant="card" count={2} />
      </main>
    </div>
  );
}










// ================================================================
// 🆕 ADMIN SUBSCRIPTION SETTINGS MODAL
// ================================================================
function AdminSubscriptionSettingsModal({ project, onClose, onSave }) {
  const [enabled, setEnabled] = useState(
    !!project.subscriptionEnabled || Number(project.subscriptionPrice) > 0
  );
  const [price, setPrice] = useState(Number(project.subscriptionPrice) || 0);
  const [durationDays, setDurationDays] = useState(Number(project.subscriptionDurationDays) || 365);
  const [graceDays, setGraceDays] = useState(Number(project.subscriptionGraceDays) || 3);

  const handleSave = () => {
    const numPrice = Math.max(0, Math.floor(Number(price) || 0));
    const numDur = Math.max(1, Math.min(3650, Math.floor(Number(durationDays) || 365)));
    const numGrace = Math.max(0, Math.min(30, Math.floor(Number(graceDays) || 3)));

    onSave({
      subscriptionEnabled: enabled && numPrice > 0,
      subscriptionPrice: numPrice,
      subscriptionDurationDays: numDur,
      subscriptionGraceDays: numGrace,
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="💰 تنظیمات اشتراک (مدیر)"
      size="md"
      footer={
        <>
          <button className="apr-btn apr-btn-ghost" onClick={onClose}>انصراف</button>
          <button className="apr-btn apr-btn-primary" onClick={handleSave}>💾 ذخیره تغییرات</button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: '#f9fafb', borderRadius: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <div>
            <div style={{ fontWeight: 700, color: '#1f2937' }}>فعال‌سازی سیستم اشتراک</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>دانش‌آموزان باید برای دسترسی هزینه پرداخت کنند</div>
          </div>
        </label>

        {enabled && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 800, color: '#334155' }}>قیمت اشتراک (تومان)</label>
              <input 
                type="text" 
                inputMode="numeric" 
                pattern="[0-9]*" 
                style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontFamily: 'inherit' }} 
                value={price || ''} 
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setPrice(val ? Number(val) : 0);
                }} 
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 800, color: '#334155' }}>مدت اشتراک (روز)</label>
              <input 
                type="text" 
                inputMode="numeric" 
                pattern="[0-9]*" 
                style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontFamily: 'inherit' }} 
                value={durationDays || ''} 
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setDurationDays(val ? Number(val) : 1);
                }} 
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 800, color: '#334155' }}>مهلت اضافه (روز)</label>
              <input 
                type="text" 
                inputMode="numeric" 
                pattern="[0-9]*" 
                style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0', fontFamily: 'inherit' }} 
                value={graceDays || ''} 
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setGraceDays(val ? Number(val) : 0);
                }} 
              />
            </div>
          </>
        )}
        <div style={{ padding: 12, background: '#fef3c7', borderRadius: 10, fontSize: 13, color: '#92400e' }}>
          ℹ️ این تغییرات روی خریدهای آینده تأثیر می‌گذارد. اشتراک‌های خریداری‌شده قبلی تا زمان انقضا معتبر می‌مانند.
        </div>
      </div>
    </Modal>
  );
}